import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Calendar as CalendarIcon, Clock, Globe2, Trash2, Plus, Star, Video, UserPlus } from "lucide-react";

const DAY_CODES = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const DURATIONS = [15, 20, 30, 45, 60];

function pretty(dtIso) {
    try {
        return new Date(dtIso).toLocaleString("en-IN", {
            dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata",
        });
    } catch {
        return dtIso;
    }
}

function emptyWeekly() {
    const o = {};
    for (const d of DAY_CODES) o[d] = [];
    return o;
}

export default function DoctorAppointments() {
    const [doctor, setDoctor] = useState(null);
    const [av, setAv] = useState({
        slot_duration_min: 30, buffer_min: 0, lead_hours: 2, window_days: 30, weekly: emptyWeekly(),
        online_enabled: false, online_only: false, meet_link_url: "",
    });
    const [appts, setAppts] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [savingAv, setSavingAv] = useState(false);
    const [place, setPlace] = useState({ place_id: "", enabled: true, platform_api_key_configured: false, help_url: "" });
    const [reviewsPreview, setReviewsPreview] = useState(null);
    const [followUpDraft, setFollowUpDraft] = useState({});   // appt_id → days

    async function loadAll() {
        try {
            const [d, a, l, p, w] = await Promise.all([
                api.get("/me/doctor"),
                api.get("/me/appointments/availability"),
                api.get("/me/appointments"),
                api.get("/me/google-reviews"),
                api.get("/me/appointments/waitlist"),
            ]);
            setDoctor(d.data);
            const weekly = emptyWeekly();
            Object.assign(weekly, a.data?.weekly || {});
            setAv({
                slot_duration_min: a.data?.slot_duration_min || 30,
                buffer_min: a.data?.buffer_min || 0,
                lead_hours: a.data?.lead_hours ?? 2,
                window_days: a.data?.window_days ?? 30,
                weekly,
                online_enabled: !!a.data?.online_enabled,
                online_only: !!a.data?.online_only,
                meet_link_url: a.data?.meet_link_url || "",
            });
            setAppts(l.data || []);
            setPlace(p.data || place);
            setWaitlist(w.data || []);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }
    useEffect(() => { loadAll(); }, []);

    function updateWindow(day, idx, field, value) {
        const copy = { ...av.weekly };
        copy[day] = [...(copy[day] || [])];
        copy[day][idx] = { ...copy[day][idx], [field]: value };
        setAv({ ...av, weekly: copy });
    }
    function addWindow(day) {
        const copy = { ...av.weekly };
        copy[day] = [...(copy[day] || []), { from: "09:00", to: "17:00" }];
        setAv({ ...av, weekly: copy });
    }
    function removeWindow(day, idx) {
        const copy = { ...av.weekly };
        copy[day] = (copy[day] || []).filter((_, i) => i !== idx);
        setAv({ ...av, weekly: copy });
    }

    async function saveAvailability() {
        setSavingAv(true);
        try {
            // Strip empty day arrays for a cleaner payload.
            const weekly = {};
            for (const d of DAY_CODES) if ((av.weekly[d] || []).length) weekly[d] = av.weekly[d];
            await api.put("/me/appointments/availability", {
                slot_duration_min: Number(av.slot_duration_min),
                buffer_min: Number(av.buffer_min),
                lead_hours: Number(av.lead_hours),
                window_days: Number(av.window_days),
                weekly,
                online_enabled: !!av.online_enabled,
                online_only: !!av.online_only,
                meet_link_url: av.meet_link_url?.trim() || "",
            });
            toast.success("Availability saved");
            loadAll();
        } catch (e) {
            toast.error(errMsg(e, "Could not save"));
        } finally {
            setSavingAv(false);
        }
    }

    async function updateApptStatus(id, status, extraFields = {}) {
        try {
            await api.put(`/me/appointments/${id}`, { status, ...extraFields });
            toast.success("Updated");
            loadAll();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    async function markDoneWithFollowup(id) {
        const draft = followUpDraft[id];
        const extra = {};
        if (draft != null && draft !== "" && Number(draft) > 0) {
            extra.follow_up_days = Number(draft);
        }
        await updateApptStatus(id, "completed", extra);
    }

    async function cancelFollowup(id) {
        try {
            await api.put(`/me/appointments/${id}`, { follow_up_days: 0 });
            toast.success("Follow-up cancelled");
            loadAll();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    async function removeWaitlistEntry(wid) {
        try {
            await api.delete(`/me/appointments/waitlist/${wid}`);
            toast.success("Waitlist entry removed");
            loadAll();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    async function savePlace() {
        try {
            const r = await api.put("/me/google-reviews", { place_id: place.place_id.trim(), enabled: place.enabled });
            setPlace(r.data);
            toast.success("Saved");
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }
    async function previewReviews() {
        setReviewsPreview({ loading: true });
        try {
            const r = await api.post("/me/google-reviews/preview");
            setReviewsPreview(r.data);
            if (!r.data.ok) toast.error(r.data.message || "No reviews returned");
        } catch (e) {
            setReviewsPreview(null);
            toast.error(errMsg(e, "Preview failed"));
        }
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="space-y-6" data-testid="doctor-appointments">
                <div>
                    <h1 className="text-3xl font-bold text-[#123B5D]">Appointments & Reviews</h1>
                    <p className="text-[#17212B]/60 mt-1 text-sm">
                        Set your weekly availability for public booking, manage upcoming appointments,
                        and connect Google Reviews to your public profile.
                    </p>
                </div>

                {/* Availability */}
                <Card data-testid="availability-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#123B5D]">
                            <CalendarIcon size={18} /> Weekly availability
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <label className="text-xs text-[#17212B]/70 font-semibold">Slot duration (min)</label>
                                <select data-testid="av-slot-duration"
                                    value={av.slot_duration_min}
                                    onChange={(e) => setAv({ ...av, slot_duration_min: Number(e.target.value) })}
                                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm">
                                    {DURATIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-[#17212B]/70 font-semibold">Buffer (min)</label>
                                <Input data-testid="av-buffer" type="number" min={0} max={60}
                                    value={av.buffer_min}
                                    onChange={(e) => setAv({ ...av, buffer_min: Number(e.target.value) })} />
                            </div>
                            <div>
                                <label className="text-xs text-[#17212B]/70 font-semibold">Min lead time (hours)</label>
                                <Input data-testid="av-lead-hours" type="number" min={0} max={168}
                                    value={av.lead_hours}
                                    onChange={(e) => setAv({ ...av, lead_hours: Number(e.target.value) })} />
                            </div>
                            <div>
                                <label className="text-xs text-[#17212B]/70 font-semibold">Booking window (days)</label>
                                <Input data-testid="av-window-days" type="number" min={1} max={180}
                                    value={av.window_days}
                                    onChange={(e) => setAv({ ...av, window_days: Number(e.target.value) })} />
                            </div>
                        </div>

                        <div className="space-y-3">
                            {DAY_CODES.map((d) => (
                                <div key={d} className="flex items-start gap-3 border-t pt-3" data-testid={`day-${d}`}>
                                    <div className="w-16 text-sm font-semibold text-[#123B5D] pt-2">{DAY_LABELS[d]}</div>
                                    <div className="flex-1 space-y-2">
                                        {(av.weekly[d] || []).map((w, i) => (
                                            <div key={i} className="flex items-center gap-2" data-testid={`window-${d}-${i}`}>
                                                <Input type="time" value={w.from}
                                                    onChange={(e) => updateWindow(d, i, "from", e.target.value)}
                                                    className="w-32" />
                                                <span className="text-[#17212B]/50 text-sm">to</span>
                                                <Input type="time" value={w.to}
                                                    onChange={(e) => updateWindow(d, i, "to", e.target.value)}
                                                    className="w-32" />
                                                <Button variant="ghost" size="sm" onClick={() => removeWindow(d, i)}
                                                    data-testid={`remove-window-${d}-${i}`}>
                                                    <Trash2 size={14} />
                                                </Button>
                                            </div>
                                        ))}
                                        <Button variant="outline" size="sm" onClick={() => addWindow(d)}
                                            data-testid={`add-window-${d}`} className="text-xs">
                                            <Plus size={12} className="mr-1" /> Add window
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-end pt-2">
                            <Button onClick={saveAvailability} disabled={savingAv} data-testid="save-availability">
                                {savingAv ? "Saving…" : "Save availability"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Video consultation */}
                <Card data-testid="video-consult-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#123B5D]">
                            <Video size={18} /> Video consultation
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-[#123B5D]">Allow patients to pick "Online"</div>
                                <div className="text-xs text-[#17212B]/60">A toggle appears on your public booking widget. Online bookings automatically include your meet link in the confirmation.</div>
                            </div>
                            <Switch data-testid="online-enabled-toggle"
                                checked={!!av.online_enabled}
                                onCheckedChange={(v) => setAv({ ...av, online_enabled: v })} />
                        </div>
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-[#123B5D]">Online-only (hide in-clinic option)</div>
                                <div className="text-xs text-[#17212B]/60">Use this if you consult exclusively online right now.</div>
                            </div>
                            <Switch data-testid="online-only-toggle"
                                checked={!!av.online_only}
                                onCheckedChange={(v) => setAv({ ...av, online_only: v })} />
                        </div>
                        <div>
                            <label className="text-xs text-[#17212B]/70 font-semibold">Meet link (Google Meet / Zoom / Webex / Jitsi URL)</label>
                            <Input data-testid="meet-link-input"
                                value={av.meet_link_url}
                                onChange={(e) => setAv({ ...av, meet_link_url: e.target.value })}
                                placeholder="https://meet.google.com/abc-defg-hij" />
                            <p className="text-[11px] text-[#17212B]/50 mt-1">
                                Required to accept online bookings. The link is shared only with confirmed patients — it is never shown on your public page.
                            </p>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={saveAvailability} data-testid="save-video-consult"
                                className="bg-[#0F766E] hover:bg-[#0b5a52]">Save video settings</Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Upcoming appointments */}
                <Card data-testid="appointments-list-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#123B5D]">
                            <Clock size={18} /> Upcoming appointments ({appts.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {appts.length === 0 && (
                            <div className="text-sm text-[#17212B]/60 py-6 text-center" data-testid="appointments-empty">
                                No bookings yet. Share your profile link so patients can book a slot.
                            </div>
                        )}
                        <div className="space-y-2">
                            {appts.map((a) => (
                                <div key={a.id} className="flex flex-col gap-2 border rounded-md p-3"
                                     data-testid={`appt-row-${a.id}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <div className="font-semibold text-[#123B5D] text-sm flex items-center gap-2">
                                                {a.patient_name}
                                                {a.mode === "online" && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#0F766E]/10 text-[#0F766E]">
                                                        <Video size={10} /> Online
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-[#17212B]/60">
                                                {pretty(a.slot_start_iso)} · {a.duration_min} min · {a.patient_phone}
                                            </div>
                                            {a.meet_link_url && (
                                                <a href={a.meet_link_url} target="_blank" rel="noopener noreferrer"
                                                    className="text-xs text-[#0F766E] font-mono hover:underline inline-flex items-center gap-1 mt-1"
                                                    data-testid={`appt-meet-link-${a.id}`}>
                                                    <Video size={10} /> Join meet link
                                                </a>
                                            )}
                                            {a.note && <div className="text-xs mt-1 text-[#17212B]/70">{a.note}</div>}
                                            {a.follow_up_due_at && !a.follow_up_cancelled && (
                                                <div className="text-[11px] text-[#D97706] font-semibold mt-1" data-testid={`appt-followup-${a.id}`}>
                                                    Follow-up reminder: {pretty(a.follow_up_due_at)}
                                                    {a.follow_up_sent_at ? " · sent" : " · scheduled"}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                                                a.status === "booked" ? "bg-emerald-100 text-emerald-800"
                                                : a.status === "completed" ? "bg-slate-100 text-slate-700"
                                                : a.status === "cancelled" ? "bg-rose-100 text-rose-800"
                                                : "bg-amber-100 text-amber-800"
                                            }`}>{a.status}</span>
                                            {a.status === "booked" && (
                                                <>
                                                    <Button size="sm" variant="ghost" onClick={() => updateApptStatus(a.id, "cancelled")}
                                                        data-testid={`appt-cancel-${a.id}`}>Cancel</Button>
                                                </>
                                            )}
                                            {a.status === "completed" && a.follow_up_due_at && !a.follow_up_cancelled && !a.follow_up_sent_at && (
                                                <Button size="sm" variant="ghost" onClick={() => cancelFollowup(a.id)}
                                                    data-testid={`appt-cancel-followup-${a.id}`}>Cancel follow-up</Button>
                                            )}
                                        </div>
                                    </div>
                                    {a.status === "booked" && (
                                        <div className="flex items-center gap-2 pt-1 border-t text-xs" data-testid={`appt-done-controls-${a.id}`}>
                                            <span className="text-[#17212B]/60">Mark done & remind in</span>
                                            <select value={followUpDraft[a.id] ?? ""}
                                                data-testid={`followup-days-${a.id}`}
                                                onChange={(e) => setFollowUpDraft({ ...followUpDraft, [a.id]: e.target.value })}
                                                className="border rounded px-2 py-1 text-xs">
                                                <option value="">no follow-up</option>
                                                <option value="7">7 days</option>
                                                <option value="15">15 days</option>
                                                <option value="30">30 days</option>
                                                <option value="60">60 days</option>
                                                <option value="90">90 days</option>
                                            </select>
                                            <Button size="sm" variant="outline"
                                                data-testid={`appt-done-${a.id}`}
                                                onClick={() => markDoneWithFollowup(a.id)}>
                                                Mark Done
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Waitlist */}
                <Card data-testid="waitlist-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#123B5D]">
                            <UserPlus size={18} /> Patient waitlist ({waitlist.filter(w => !w.cancelled && !w.notified_at).length} waiting)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {waitlist.length === 0 && (
                            <div className="text-sm text-[#17212B]/60 py-6 text-center" data-testid="waitlist-empty">
                                No waitlist entries. When a day is fully booked, patients can join the waitlist and get a message if a slot opens up.
                            </div>
                        )}
                        <div className="space-y-2">
                            {waitlist.filter(w => !w.cancelled).map((w) => (
                                <div key={w.id} className="flex items-center justify-between gap-3 border rounded-md p-3"
                                     data-testid={`waitlist-row-${w.id}`}>
                                    <div>
                                        <div className="font-semibold text-[#123B5D] text-sm">
                                            {w.name} · <span className="text-xs text-[#17212B]/60 font-normal">{w.date_ist}</span>
                                        </div>
                                        <div className="text-xs text-[#17212B]/60">{w.phone}{w.email ? ` · ${w.email}` : ""}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {w.notified_at ? (
                                            <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800" data-testid={`waitlist-notified-${w.id}`}>notified</span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800">waiting</span>
                                        )}
                                        <Button size="sm" variant="ghost" onClick={() => removeWaitlistEntry(w.id)}
                                            data-testid={`waitlist-remove-${w.id}`}>Remove</Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Google Reviews */}
                <Card data-testid="google-reviews-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-[#123B5D]">
                            <Star size={18} /> Google Reviews on your public profile
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {!place.platform_api_key_configured && (
                            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"
                                 data-testid="reviews-platform-warning">
                                Platform Google Places API key isn't configured yet. Reviews will stay hidden on your
                                public profile until the platform admin enables it — your Place ID is still saved.
                            </div>
                        )}
                        <div>
                            <label className="text-xs text-[#17212B]/70 font-semibold">Google Place ID</label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input data-testid="place-id-input" value={place.place_id}
                                    onChange={(e) => setPlace({ ...place, place_id: e.target.value })}
                                    placeholder="e.g. ChIJN1t_tDeuEmsRUsoyG83frY4" />
                                <Switch checked={!!place.enabled} onCheckedChange={(v) => setPlace({ ...place, enabled: v })}
                                    data-testid="place-enabled-toggle" />
                            </div>
                            <a href={place.help_url || "https://developers.google.com/maps/documentation/places/web-service/place-id"}
                                target="_blank" rel="noopener noreferrer"
                                className="text-xs text-[#0F766E] font-semibold hover:underline inline-flex items-center gap-1 mt-2"
                                data-testid="place-id-help-link">
                                <Globe2 size={12} /> How to find your Place ID
                            </a>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={savePlace} data-testid="save-place-id">Save</Button>
                            <Button variant="outline" onClick={previewReviews}
                                disabled={!place.place_id || !place.platform_api_key_configured}
                                data-testid="preview-reviews">Preview</Button>
                        </div>
                        {reviewsPreview && reviewsPreview.ok && (
                            <div className="rounded-md border p-3 text-sm space-y-2" data-testid="reviews-preview">
                                <div>
                                    <strong>{reviewsPreview.name || "Place"}</strong> · {reviewsPreview.rating?.toFixed(1)}/5
                                    · {reviewsPreview.total} reviews
                                </div>
                                {(reviewsPreview.reviews || []).slice(0, 3).map((rv, i) => (
                                    <div key={i} className="text-xs text-[#17212B]/80 pl-2 border-l-2 border-[#0F766E]/40">
                                        <div className="font-semibold">{rv.author_name} ({rv.rating}/5)</div>
                                        <div>{rv.text?.slice(0, 160)}{rv.text?.length > 160 ? "…" : ""}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DoctorShell>
    );
}
